<!--
Swipeable carousel component. isPrimary prop determines which markdown to use.

A left-chevron will show on the left, unless the user is looking at the first (leftmost) item.
A right-chevron will show on the right, unless the user is looking at the last (rightmost) item.
No chevrons will appear if there is one item in the list.

@author crm4f
-->
<template>
  <div>
    <div v-if="isPrimary" class="primaryCarouselContainer">
      <button :disabled="buttonsDisabled" class='displayAtLeft' :style="{ visibility: currentItem == 0 ? 'hidden' : 'visible' }" @click="slideItem(-1)" aria-label="Previous">
        <font-awesome-icon style="color:#003685" icon="chevron-left" size="2xl"></font-awesome-icon>
      </button>
      <div class="componentDiv scrollover">
        <div :id="name + 'carousel'" class="itemContainer">
          <slot></slot>
        </div>
      </div>
      <button :disabled="buttonsDisabled" class='displayAtRight' :style="{ visibility: currentItem == itemNames.length -1 ? 'hidden' : 'visible' }" @click="slideItem(1)" aria-label="Next">
        <font-awesome-icon style="color:#003685" icon="chevron-right" size="2xl"></font-awesome-icon>
      </button>
    </div>

    <div class="secondaryCarouselContainer" v-else>
      <div class="componentDiv scrollover" style="width:100%;">
        <div :id="name + 'carousel'" class="itemContainer">
          <slot></slot>
        </div>
      </div>
      <div class="controlContainer">
        <button :disabled="buttonsDisabled" class='displayAtLeft' :style="{ visibility: currentItem == 0 ? 'hidden' : 'visible' }" @click="slideItem(-1)" aria-label="Previous">
            <font-awesome-icon style="color:#fff" icon="chevron-left" size="2xl"></font-awesome-icon>
        </button>
        <div id="dotList">
          <div v-bind:key="index" v-for="(item, index) in itemNames" class="dot" :class="{ dotActive: currentItem == index, disabledDots: buttonsDisabled }" @click="slideItem(index - currentItem)"></div>
        </div>
        <button :disabled="buttonsDisabled" class='displayAtRight' :style="{ visibility: currentItem == itemNames.length -1 ? 'hidden' : 'visible' }" @click="slideItem(1)" aria-label="Next">
            <font-awesome-icon style="color:#fff" icon="chevron-right" size="2xl"></font-awesome-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>

import { onMounted, ref } from 'vue'

const props = defineProps({
  name: String,
  isPrimary: Boolean
})

const itemName = props.name + '0'

// const swipe = ref(false)
const currentItem = ref(0)
const itemNames = ref([])
const buttonsDisabled = ref(false)
const itemOnScreen = ref(itemName)

onMounted(() => {
    findSwipeItems()
    // let _this = this
    // swiping - touch, middle-mouse, trackpads
    let returnedFunction = debounce(function () {
      handleSwipeEnd()
    }, 50)
    document.querySelector('.' + props.name + 'CarouselContainer' + ' .componentDiv.scrollover').addEventListener('scroll', returnedFunction)
    deselectOld()
  })

    // debouncing prevents the scroll event from calling constantly, only calling once when its completed...
  function  debounce (func, wait = 50) {
      let timeout
      // function to be returned:
      return function executedFunction (...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    }

    function findSwipeItems () {
      itemNames.value = []
      const swipe = document.getElementById(props.name + 'carousel')
      if (typeof swipe !== 'undefined') {
        for (let elem of swipe.children) {
          itemNames.value.push(elem.id)
        }
      } else {
        console.error('Element for Swipe for ' + props.name + ' not found!')
      }
    }

    function handleSwipeEnd () {
      // wait 100ms for swipe to finish, then get active item on screen and update this component accordingly
      setTimeout(() => {
        itemOnScreen.value = findActiveItem()
        if (itemOnScreen.value != undefined) {
          let itemOnScreenIndex = Number(itemOnScreen.value.replace(props.name, ''))
          slideItem(itemOnScreenIndex - currentItem.value)
        }
      }, 100)
    }

    // scroll to desired item
    function slideItem (moveBy) {
      // as to stop button spamming :)
      disableButtons()
      if ((moveBy < 0 && currentItem.value > 0) || ((moveBy > 0 && currentItem.value < itemNames.value.length - 1))) {
        currentItem.value += moveBy
        scroll(calculateScroll())
      }
      deselectOld()
    }

    function scroll (to) {
      let carousel = document.querySelector('.' + props.name + 'CarouselContainer' + ' .componentDiv.scrollover')
      // iOS does not support CSS scroll behaviour smooth... a rather crude work around :/
      let smoothScrollFeature = 'scrollBehavior' in document.documentElement.style
      let i = parseInt(carousel.scrollLeft)
      if (i != to) {
        if (!smoothScrollFeature) {
          to = parseInt(to)
          if (i < to) {
            let int = setInterval(function () {
              if (i > (to - 20)) i += 1
              else if (i > (to - 40)) i += 3
              else if (i > (to - 80)) i += 5
              else if (i > (to - 160)) i += 8
              else if (i > (to - 200)) i += 13
              else if (i > (to - 300)) i += 21
              else i += 34
              carousel.scrollTo({ left: i, top: 0 })
              if (i >= to) clearInterval(int)
            }, 15)
          } else {
            let int = setInterval(function () {
              if (i < (to + 20)) i -= 1
              else if (i < (to + 40)) i -= 3
              else if (i < (to + 80)) i -= 5
              else if (i < (to + 160)) i -= 8
              else if (i < (to + 200)) i -= 13
              else if (i < (to + 300)) i -= 21
              else i -= 34
              carousel.scrollTo({ left: i, top: 0 })
              if (i <= to) clearInterval(int)
            }, 15)
          }
        } else {
          carousel.scrollTo({ left: to, top: 0, behavior: 'smooth' })
        }
      }
    }

    function calculateScroll () {
      return document.querySelector('.' + props.name + 'CarouselContainer' + ' .componentDiv.scrollover').offsetWidth * currentItem.value
    }
    // retrieve the item on screen (important for swiping)
    
    function findActiveItem () {
      const items = document.querySelectorAll('.' + props.name + 'Container' + ' .carouselItem')
      for (let i = 0; i < items.length; i++) {
        // checking if carouselItem is taking up the correct position
        if (Math.abs(items[i].getBoundingClientRect().left - document.querySelector('.' + props.name + 'CarouselContainer' + ' .componentDiv.scrollover').getBoundingClientRect().left) < 10) {
          return items[i].id
        }
      }
    }

    function disableButtons () {
      buttonsDisabled.value = true
      setTimeout(() => {
        buttonsDisabled.value = false
      }, 300)
    }
    
    function deselectOld () {
      const sel = itemNames.value[currentItem.value]
      for (let name of itemNames.value) {
        let item = document.getElementById(name)
        if (name === sel) {
          item.classList.remove('itemNotSelected')
          item.classList.add('itemSelected')
        } else {
          item.classList.remove('itemSelected')
          item.classList.add('itemNotSelected')
        }
      }
    }

</script>
<style lang="scss">

.primaryCarouselContainer {
  display: flex;
  justify-content: center;
  align-items: center;
}

.secondaryCarouselContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
}

.itemContainer {
  min-height: fit-content;
  width: 100%;
}

.controlContainer {
  display: flex;
  width: 100%;
}

.disabledDots {
  pointer-events: none;
}

.dot {
  display: inline-block;
  border: 2px solid #fff;
  height: 15px;
  border-radius: 50%;
  -moz-border-radius: 50%;
  -webkit-border-radius: 50%;
  width: 15px;
  margin: 5px;
  background: transparent;
  cursor: pointer;
}

.dotActive {
  background-color: #fff;
}

#dotList {
  display: flex;
  align-items: center;
  justify-content: center;
}

.componentDiv {
  overflow: hidden;
  padding: 3px 3px 3px 0px;
  align-items: center;
  display: flex;
  width: fit-content;
  margin: auto;
}
div.scrollover {
  overflow-y:hidden;
  overflow-x: scroll;
  white-space: nowrap;

  scroll-snap-type: x mandatory;

  -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}

.scrollover::-webkit-scrollbar {
  display: none;
}

.displayAtLeft:focus, .displayAtRight:focus {
  outline: none;
}

.displayAtLeft, .displayAtRight {
  z-index:99;
  border: 0;
  background-color: transparent;
  font-size: 10px;
  width: fit-content;
  height: fit-content;
}

.displayAtLeft {
  margin-right: auto;
  padding: 20px 20px 20px 0px;
}

.displayAtRight {
  padding: 20px 0px 20px 20px;
  margin-left: auto;
}

.carouselItem {
  white-space: pre;
  display: inline-flex;
  width: 100%;
  min-width: fit-content;
  justify-content: center;
  align-items: center;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  margin-right: 3% !important;
}

</style>
